// pages/formularios/BlocoEditor.js — um bloco da tela no construtor.
//
// Out/26. Cartão com número, tipo, Obrigatório/Opcional e mover/apagar —
// o mesmo cabeçalho do design aprovado — e o corpo que muda pelo tipo:
// rótulo; opções (escolhas); estilo e texto (Texto); arquivo (Imagem);
// descrição e quantidade (Foto/Documento do cliente). Os limites da Meta
// aparecem como contador ao lado de cada texto, e o problema aparece
// embaixo do campo que precisa mudar.

import { useRef, useState } from 'react';
import {
  Box, Button, IconButton, ListSubheader, MenuItem, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { FiArrowDown, FiArrowUp, FiImage, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import {
  ANEXOS, ESCOLHAS, ESTILOS_TEXTO, GRUPOS, LIMITE, TIPOS,
  comprimirImagem, ehResposta, limiteRotulo, limiteTexto, maxOpcoes,
} from './regras';
import { ajuda, botaoTexto, campo, menuPapel } from './estilo';

const contador = (n, max) => `${n}/${max}`;

function Erros({ lista }) {
  if (!lista.length) return null;
  return (
    <Box sx={{ mt: 0.8 }}>
      {lista.map((p, k) => (
        <Typography key={k} role="alert" sx={{ fontSize: '0.74rem', color: 'var(--fm-red)', fontWeight: 600 }}>{p.mensagem}</Typography>
      ))}
    </Box>
  );
}

export function opcoesDeTipo() {
  const itens = [];
  GRUPOS.forEach((g) => {
    itens.push(<ListSubheader key={`g-${g}`}>{g}</ListSubheader>);
    TIPOS.filter((t) => t.grupo === g).forEach((t) => itens.push(<MenuItem key={t.v} value={t.v}>{t.l}</MenuItem>));
  });
  return itens;
}

export default function BlocoEditor({
  bloco, numero, primeiro, ultimo, problemas, onChange, onTrocarTipo, onMover, onApagar,
}) {
  const b = bloco;
  const arquivo = useRef(null);
  const [erroImagem, setErroImagem] = useState('');
  const doCampo = (c) => problemas.filter((p) => p.campo === c);
  const gerais = problemas.filter((p) => !['rotulo', 'opcoes', 'texto', 'src', 'maximo', 'destino'].includes(p.campo));
  const mudar = (patch) => onChange({ ...b, ...patch });

  const escolherImagem = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!/^image\/(jpeg|png)$/.test(f.type)) { setErroImagem('Use uma imagem JPG ou PNG.'); return; }
    try {
      setErroImagem('');
      const img = await comprimirImagem(f);
      mudar(img);
    } catch {
      setErroImagem('Não deu para reduzir esta imagem a 300 KB. Use uma menor.');
    }
  };

  const rotuloMax = limiteRotulo(b.tipo);
  const erroRotulo = doCampo('rotulo')[0];

  return (
    <Box
      data-bloco={b.id}
      sx={{
        border: '1px solid', borderColor: problemas.length ? 'var(--fm-red-b)' : 'var(--fm-bdr)',
        borderRadius: '10px', p: 1.75, bgcolor: 'var(--fm-paper)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'var(--fm-chip)', color: 'var(--fm-chip-t)', fontSize: '0.72rem', fontWeight: 700, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          {numero}
        </Box>
        <TextField
          select size="small" value={b.tipo} onChange={(e) => onTrocarTipo(e.target.value)}
          SelectProps={{ MenuProps: menuPapel }} inputProps={{ 'aria-label': `Tipo do bloco ${numero}` }}
          sx={{ ...campo, minWidth: 150, flex: { xs: 1, sm: '0 0 auto' } }}
        >
          {opcoesDeTipo()}
        </TextField>
        {ehResposta(b.tipo) && (
          <ToggleButtonGroup
            size="small" exclusive value={b.obrigatorio ? 'sim' : 'nao'}
            onChange={(_, v) => { if (v) mudar({ obrigatorio: v === 'sim' }); }}
            aria-label="Obrigatório ou opcional"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none', px: 1.1, py: 0.45, fontSize: '0.78rem', color: 'var(--fm-ink2)', borderColor: 'var(--fm-line)',
                '&.Mui-selected': { bgcolor: 'var(--fm-sel)', color: 'var(--fm-ink)', fontWeight: 700 },
                '&:focus-visible': { outline: '2px solid var(--fm-focus)', outlineOffset: 1 },
              },
            }}
          >
            <ToggleButton value="sim">Obrigatório</ToggleButton>
            <ToggleButton value="nao">Opcional</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Subir"><span><IconButton size="small" aria-label="Subir bloco" disabled={primeiro} onClick={() => onMover(-1)} sx={{ color: 'var(--fm-ink2)' }}><FiArrowUp size={15} /></IconButton></span></Tooltip>
        <Tooltip title="Descer"><span><IconButton size="small" aria-label="Descer bloco" disabled={ultimo} onClick={() => onMover(1)} sx={{ color: 'var(--fm-ink2)' }}><FiArrowDown size={15} /></IconButton></span></Tooltip>
        <Tooltip title="Apagar"><IconButton size="small" aria-label="Apagar bloco" onClick={onApagar} sx={{ color: 'var(--fm-red)' }}><FiTrash2 size={15} /></IconButton></Tooltip>
      </Box>

      <Box sx={{ mt: 1.75, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {b.tipo === 'conteudo' && (
          <>
            <TextField
              select size="small" label="Estilo" value={b.estilo || 'paragrafo'}
              onChange={(e) => mudar({ estilo: e.target.value })}
              SelectProps={{ MenuProps: menuPapel }} sx={{ ...campo, maxWidth: 220 }}
            >
              {ESTILOS_TEXTO.map((e) => <MenuItem key={e.v} value={e.v}>{e.l}</MenuItem>)}
            </TextField>
            <TextField
              label="Texto" value={b.texto || ''} onChange={(e) => mudar({ texto: e.target.value })}
              multiline minRows={b.estilo === 'paragrafo' || b.estilo === 'legenda' ? 2 : 1}
              error={Boolean(doCampo('texto')[0])}
              helperText={doCampo('texto')[0]?.mensagem || contador((b.texto || '').trim().length, limiteTexto(b.estilo || 'paragrafo'))}
              FormHelperTextProps={{ sx: { textAlign: doCampo('texto')[0] ? 'left' : 'right' } }}
              sx={campo} fullWidth
            />
          </>
        )}

        {b.tipo === 'imagem' && (
          <>
            <input ref={arquivo} type="file" accept="image/jpeg,image/png" hidden onChange={escolherImagem} />
            {b.src ? (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box component="img" src={b.src} alt={b.descricao || 'Imagem do formulário'} sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--fm-bdr)' }} />
                <Button size="small" onClick={() => arquivo.current?.click()} sx={botaoTexto}>Trocar imagem</Button>
              </Box>
            ) : (
              <Box
                component="button" type="button" onClick={() => arquivo.current?.click()}
                sx={{
                  border: '1px dashed var(--fm-bdr-h)', borderRadius: '10px', bgcolor: 'var(--fm-bg)', color: 'var(--fm-body)',
                  py: 2.5, cursor: 'pointer', font: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.6,
                  '&:hover': { borderColor: 'var(--fm-green)', color: 'var(--fm-green-t)' },
                  '&:focus-visible': { outline: '2px solid var(--fm-focus)', outlineOffset: 2 },
                }}
              >
                <FiImage size={20} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.84rem' }}>Escolher imagem</Typography>
                <Typography sx={ajuda}>JPG ou PNG. A Cetto reduz para até 300 KB.</Typography>
              </Box>
            )}
            <Erros lista={erroImagem ? [{ mensagem: erroImagem }] : doCampo('src')} />
            <TextField
              size="small" label="Descrição da imagem" value={b.descricao || ''}
              onChange={(e) => mudar({ descricao: e.target.value })}
              helperText="Lida em voz alta para quem usa leitor de tela."
              sx={campo} fullWidth
            />
          </>
        )}

        {ehResposta(b.tipo) && (
          <TextField
            label="Rótulo" value={b.rotulo || ''} onChange={(e) => mudar({ rotulo: e.target.value })}
            error={Boolean(erroRotulo)}
            helperText={erroRotulo?.mensagem || contador((b.rotulo || '').trim().length, rotuloMax)}
            FormHelperTextProps={{ sx: { textAlign: erroRotulo ? 'left' : 'right' } }}
            sx={campo} fullWidth
          />
        )}

        {ESCOLHAS.includes(b.tipo) && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--fm-label)' }}>Opções</Typography>
              <Typography sx={ajuda}>{contador((b.opcoes || []).length, maxOpcoes(b.tipo))}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(b.opcoes || []).map((op, k) => (
                <Box key={k} sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                  <TextField
                    size="small" value={op} placeholder={`Opção ${k + 1}`}
                    onChange={(e) => mudar({ opcoes: b.opcoes.map((o, j) => (j === k ? e.target.value : o)) })}
                    inputProps={{ 'aria-label': `Opção ${k + 1}`, maxLength: 60 }}
                    sx={{ ...campo, flex: 1 }}
                  />
                  <Typography sx={{ ...ajuda, minWidth: 38, textAlign: 'right' }}>{contador(String(op).trim().length, LIMITE.opcao)}</Typography>
                  <IconButton size="small" aria-label={`Apagar opção ${k + 1}`} onClick={() => mudar({ opcoes: b.opcoes.filter((_, j) => j !== k) })} sx={{ color: 'var(--fm-ink3)' }}>
                    <FiX size={14} />
                  </IconButton>
                </Box>
              ))}
            </Box>
            <Button
              size="small" startIcon={<FiPlus size={13} />} sx={{ ...botaoTexto, mt: 0.75 }}
              disabled={(b.opcoes || []).length >= maxOpcoes(b.tipo)}
              onClick={() => mudar({ opcoes: [...(b.opcoes || []), ''] })}
            >
              Adicionar opção
            </Button>
            <Erros lista={doCampo('opcoes')} />
          </Box>
        )}

        {ANEXOS.includes(b.tipo) && (
          <>
            <TextField
              size="small" label="Descrição (opcional)" value={b.descricao || ''}
              onChange={(e) => mudar({ descricao: e.target.value })}
              helperText="Aparece embaixo do rótulo, no WhatsApp." sx={campo} fullWidth
            />
            <TextField
              select size="small" label="Quantos arquivos" value={b.maximo || 1}
              onChange={(e) => mudar({ maximo: Number(e.target.value) })}
              SelectProps={{ MenuProps: menuPapel }} sx={{ ...campo, maxWidth: 220 }}
              helperText={doCampo('maximo')[0]?.mensagem || 'Cada arquivo pode ter até 25 MB.'}
              error={Boolean(doCampo('maximo')[0])}
            >
              {[1, 2, 3, 5, 10].map((n) => <MenuItem key={n} value={n}>{n === 1 ? 'Até 1 arquivo' : `Até ${n} arquivos`}</MenuItem>)}
            </TextField>
            <Typography sx={ajuda}>
              {b.tipo === 'foto' ? 'O cliente tira ou escolhe a foto' : 'O cliente escolhe um PDF ou uma imagem'} na última tela, e o arquivo fica junto da resposta.
            </Typography>
          </>
        )}

        <Erros lista={gerais} />
      </Box>
    </Box>
  );
}
